import { cn } from "@/lib/utils";
import { CaretDownIcon } from "@phosphor-icons/react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import type { ComponentPropsWithRef } from "react";
import type * as React from "react";
import { Checkbox } from "./forms/checkbox";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof AccordionPrimitive.Item>) => (
  <AccordionPrimitive.Item
    ref={ref}
    data-slot="accordion-item"
    className={cn("border-b border-gray-100", className)}
    {...props}
  />
);
AccordionItem.displayName = "AccordionItem";

const baseTriggerStyles =
  "flex flex-1 items-center justify-between py-2.5 font-medium transition-all text-left [&[data-state=open]>svg]:rotate-180";
const sizeStyles = {
  sm: "text-xxs",
  md: "text-sm",
  default: "text-base",
} as const;

type AccordionTriggerProps = ComponentPropsWithRef<
  typeof AccordionPrimitive.Trigger
> & {
  "data-state"?: "open" | "closed";
  size?: keyof typeof sizeStyles;
};

const BaseAccordionTrigger = ({
  className,
  children,
  size = "default",
  ref,
  ...props
}: AccordionTriggerProps) => (
  <AccordionPrimitive.Trigger
    ref={ref}
    data-slot="accordion-trigger"
    className={cn(baseTriggerStyles, sizeStyles[size], className)}
    {...props}
  >
    <span>{children}</span>
    <CaretDownIcon
      className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200"
      aria-hidden="true"
    />
  </AccordionPrimitive.Trigger>
);

const AccordionTrigger = (props: AccordionTriggerProps) => (
  <AccordionPrimitive.Header className="flex">
    <BaseAccordionTrigger {...props} />
  </AccordionPrimitive.Header>
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = ({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithRef<typeof AccordionPrimitive.Content>) => (
  <AccordionPrimitive.Content
    ref={ref}
    data-slot="accordion-content"
    className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn("pb-4 pt-0", className)}>{children}</div>
  </AccordionPrimitive.Content>
);
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

const CheckboxAccordionTrigger = ({
  className,
  children,
  checkboxId,
  checkboxProps,
  size = "sm",
  ref,
  ...props
}: AccordionTriggerProps & {
  checkboxId: string;
  checkboxProps?: React.ComponentPropsWithoutRef<typeof Checkbox>;
}) => (
  <AccordionPrimitive.Header className="flex">
    <div className="flex items-center gap-2 w-full">
      <Checkbox
        id={checkboxId}
        variant={checkboxProps?.variant || "secondary"}
        {...checkboxProps}
      />
      <BaseAccordionTrigger size={size} ref={ref} {...props}>
        {children}
      </BaseAccordionTrigger>
    </div>
  </AccordionPrimitive.Header>
);
CheckboxAccordionTrigger.displayName = "CheckboxAccordionTrigger";

/**
 * AccordionTrigger with action items that render between the title and collapse caret.
 * Actions won't trigger accordion collapse when clicked.
 * Layout: [Title] [Actions] [Caret]
 */
const AccordionTriggerWithActions = ({
  className,
  children,
  actions,
  size = "default",
  ref,
  ...props
}: AccordionTriggerProps & {
  actions?: React.ReactNode;
}) => (
  <AccordionPrimitive.Header className="flex relative">
    <AccordionPrimitive.Trigger
      ref={ref}
      data-slot="accordion-trigger"
      className={cn(
        "flex flex-1 items-center gap-2 py-2.5 font-medium transition-all text-left [&[data-state=open]>svg:last-child]:rotate-180",
        sizeStyles[size],
        className,
      )}
      {...props}
    >
      <span className="flex-1">{children}</span>
      <CaretDownIcon
        className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200"
        aria-hidden="true"
      />
    </AccordionPrimitive.Trigger>
    {actions && (
      <div className="absolute right-5 top-1/2 -translate-y-1/2">{actions}</div>
    )}
  </AccordionPrimitive.Header>
);
AccordionTriggerWithActions.displayName = "AccordionTriggerWithActions";

export {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  CheckboxAccordionTrigger,
  AccordionTriggerWithActions,
};
